/**
 * Semáforo (item SEMAPHORE): o formato do conteúdo e as opções de exibição.
 *
 * `value` do item é `{ scale, items }`: a escala de três estados com as palavras que o
 * usuário escolheu e os itens avaliados (nome, avaliação e, quando há, uma descrição curta),
 * tudo texto puro. Só existe o formato lista (o de tabela foi retirado em 2026-09-24; a
 * config `semaphoreStyle` antiga é ignorada); o resumo (barra e contagem) sai sempre no
 * topo. O PDF (PdfEngine e `pdf-renderer/`) desenha com estes mesmos valores; o editor, a
 * lixeira, os favoritos, a prévia antiga e o app também.
 */
export type SemaphoreStatus = 'ok' | 'warn' | 'bad';

export interface SemaphoreScaleEntry {
    key: SemaphoreStatus;
    label: string;
}

export interface SemaphoreItem {
    label: string;
    status: SemaphoreStatus | null;
    /** Texto curto embaixo do nome (uma linha). Ausente => sem descrição. */
    description?: string;
}

export interface DocumentSemaphoreValue {
    scale: SemaphoreScaleEntry[];
    items: SemaphoreItem[];
}

export const SEMAPHORE_KEYS: readonly SemaphoreStatus[] = ['ok', 'warn', 'bad'];

/** Mesmos tetos da API (semaphore-value.util). */
export const SEMAPHORE_LIMITS = {
    MAX_ITEMS: 30,
    MAX_LABEL_LENGTH: 80,
    MAX_SCALE_LABEL_LENGTH: 24,
    MAX_DESCRIPTION_LENGTH: 300,
    INITIAL_ITEMS: 3,
} as const;

/** Chaves de tradução das palavras padrão da escala e dos modelos prontos. */
export const SEMAPHORE_SCALE_LABEL_KEYS: Record<SemaphoreStatus, string> = {
    ok: 'document.edit.semaphoreCard.scaleOk',
    warn: 'document.edit.semaphoreCard.scaleWarn',
    bad: 'document.edit.semaphoreCard.scaleBad',
};

export function emptySemaphoreItem(): SemaphoreItem {
    return { label: '', status: null };
}

/** Valor de um campo recém-criado: escala com as palavras dadas e três itens vazios. */
export function initialSemaphoreValue(
    labels: Record<SemaphoreStatus, string>
): DocumentSemaphoreValue {
    return {
        scale: SEMAPHORE_KEYS.map(key => ({ key, label: labels[key] })),
        items: Array.from({ length: SEMAPHORE_LIMITS.INITIAL_ITEMS }, emptySemaphoreItem),
    };
}

export function isSemaphoreStatus(value: unknown): value is SemaphoreStatus {
    return typeof value === 'string' && (SEMAPHORE_KEYS as readonly string[]).includes(value);
}

/** Lê o `value` gravado; a escala sai sempre com as três chaves na ordem ok, warn, bad. */
export function parseSemaphoreValue(raw: unknown): DocumentSemaphoreValue {
    const record =
        raw !== null && typeof raw === 'object' && !Array.isArray(raw)
            ? (raw as Record<string, unknown>)
            : {};

    const labels: Partial<Record<SemaphoreStatus, string>> = {};
    for (const entry of Array.isArray(record['scale']) ? (record['scale'] as unknown[]) : []) {
        if (entry === null || typeof entry !== 'object') continue;
        const e = entry as Record<string, unknown>;
        if (!isSemaphoreStatus(e['key']) || labels[e['key']] !== undefined) continue;
        labels[e['key']] = typeof e['label'] === 'string' ? e['label'] : '';
    }

    const items: SemaphoreItem[] = [];
    for (const entry of Array.isArray(record['items']) ? (record['items'] as unknown[]) : []) {
        if (entry === null || typeof entry !== 'object') continue;
        const e = entry as Record<string, unknown>;
        const description = typeof e['description'] === 'string' ? e['description'] : '';
        items.push({
            label: typeof e['label'] === 'string' ? e['label'] : '',
            status: isSemaphoreStatus(e['status']) ? e['status'] : null,
            ...(description ? { description } : {}),
        });
    }

    return { scale: SEMAPHORE_KEYS.map(key => ({ key, label: labels[key] ?? '' })), items };
}

/** O item imprime? (mesma regra do PDF: nome, avaliação ou descrição). */
export function semaphoreItemHasContent(item: SemaphoreItem): boolean {
    return !!item.label.trim() || item.status !== null || !!item.description?.trim();
}

/** Texto como a API grava: uma linha só, aparado e cortado no teto. */
export function cleanSemaphoreText(text: string, max: number): string {
    return text
        .replace(/\s*[\r\n]+\s*/g, ' ')
        .trim()
        .slice(0, max)
        .trim();
}

/**
 * Contagem por estado entre os itens que imprimem (a base da barra do resumo). A barra usa
 * as contagens como PESO (flex), nunca porcentagem arredondada; `unrated` é o espaçador.
 */
export function semaphoreCounts(items: SemaphoreItem[]): {
    total: number;
    unrated: number;
    counts: Record<SemaphoreStatus, number>;
} {
    const printable = items.filter(semaphoreItemHasContent);
    const counts: Record<SemaphoreStatus, number> = { ok: 0, warn: 0, bad: 0 };
    for (const item of printable) if (item.status) counts[item.status]++;
    return { total: printable.length, unrated: printable.filter(i => !i.status).length, counts };
}

/** Dois valores iguais (evita salvar o que não mudou). */
export function sameSemaphoreValue(a: DocumentSemaphoreValue, b: DocumentSemaphoreValue): boolean {
    if (a.items.length !== b.items.length) return false;
    if (a.scale.some((s, i) => s.key !== b.scale[i]?.key || s.label !== b.scale[i]?.label))
        return false;
    return a.items.every(
        (item, i) =>
            item.label === b.items[i].label &&
            item.status === b.items[i].status &&
            (item.description ?? '') === (b.items[i].description ?? '')
    );
}
